import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

export const useSubscriptionLimits = () => {
  const { user } = useAuth();
  const [limits, setLimits] = useState({
    cards: { current: 0, max: -1, canCreate: true, nonPremiumCount: 0 },
    pipelines: { current: 0, max: 1, canCreate: true },
    deals: { current: 0, max: 50, canCreate: true },
    scans: { current: 0, max: 50, canCreate: true },
    tier: 'free',
    isSubscribed: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLimits = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Fetch user subscription status
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/subscription-status`, {
          headers: {
            'Authorization': `Bearer ${await user.getIdToken()}`
          }
        });

        // Also fetch user's cards to check card-level premium
        const cardsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cards/my-cards`, {
          headers: {
            'Authorization': `Bearer ${await user.getIdToken()}`
          }
        });

        let cardCount = 0;
        let nonPremiumCardCount = 0;
        let canCreateCard = true;

        if (cardsResponse.ok) {
          const cards = await cardsResponse.json();
          cardCount = cards.length;
          
          // Check each card's premium status
          const now = new Date();
          for (const card of cards) {
            const isPremiumCard = card.is_premium_card === true;
            const premiumExpiresAt = card.premium_expires_at ? new Date(card.premium_expires_at) : null;
            const hasActivePremium = isPremiumCard && premiumExpiresAt && premiumExpiresAt > now;
            
            if (!hasActivePremium) {
              nonPremiumCardCount++;
            }
          }
          
          // Can create card if: 0 cards OR all cards have active premium
          canCreateCard = cardCount === 0 || nonPremiumCardCount === 0;
        }

        if (response.ok) {
          const data = await response.json();
          
          // Account-level subscription (for CRM/Scan features)
          const isSubscribed = data.tier !== 'free' && data.status === 'active';
          
          setLimits({
            cards: {
              current: cardCount,
              max: -1,  // No hard limit on cards
              canCreate: canCreateCard,
              nonPremiumCount: nonPremiumCardCount
            },
            pipelines: {
              current: data.pipelineCount || 0,
              max: isSubscribed ? -1 : 1,
              canCreate: isSubscribed || (data.pipelineCount || 0) < 1
            },
            deals: {
              current: data.dealCount || 0,
              max: isSubscribed ? -1 : 50,
              canCreate: isSubscribed || (data.dealCount || 0) < 50
            },
            scans: {
              current: data.scanCount || 0,
              max: isSubscribed ? -1 : 50,
              canCreate: isSubscribed || (data.scanCount || 0) < 50
            },
            team_members: data.memberLimit || 1,
            tier: data.tier || 'free',
            isSubscribed
          });
        }
      } catch (error) {
        console.error('Error fetching subscription limits:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLimits();
  }, [user]);

  const checkLimit = (type) => {
    return limits[type]?.canCreate || false;
  };

  const getLimitInfo = (type) => {
    return limits[type] || { current: 0, max: 0, canCreate: false };
  };

  return {
    limits,
    loading,
    checkLimit,
    getLimitInfo,
    isSubscribed: limits.isSubscribed,
    tier: limits.tier
  };
};
